import { publicApi } from "./api";

/**
 * Servicio para manejar todas las operaciones de asistencia
 * Actualizado para el flujo multi-paso con campos extendidos
 * No requiere autenticación pero sí token QR válido
 */
export const AttendanceService = {
  /**
   * Valida el token QR recibido en la URL
   * @param {string} token - Token QR a validar
   * @returns {Promise} Respuesta de validación
   */
  validateQRToken: async (token) => {
    try {
      const response = await publicApi.post("/attendance/validate-qr", { token });
      return response.data;
    } catch (error) {
      console.error("Error validando token QR:", error);
      throw error.response?.data || { 
        success: false, 
        message: "Error al validar código QR" 
      };
    }
  },

  /**
   * Busca un usuario por número de documento con información extendida
   * @param {string} documentNumber - Número de documento
   * @param {string} qrToken - Token QR para autorización
   * @returns {Promise} Datos completos del usuario incluyendo historial
   */
  searchUserByDocument: async (documentNumber, qrToken) => {
    try {
      const response = await publicApi.post(
        "/attendance/search-user",
        { documentNumber },
        {
          params: { tk: qrToken }
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error buscando usuario:", error);
      throw error.response?.data || { 
        success: false, 
        message: "Error al buscar usuario en el sistema" 
      };
    }
  },

  /**
   * Registra una entrada o salida con formulario extendido
   * @param {Object} attendanceData - Datos completos de asistencia
   * @param {string} qrToken - Token QR para autorización
   * @param {string} captchaToken - Token de reCAPTCHA
   * @returns {Promise} Confirmación del registro
   */
  registerAttendance: async (attendanceData, qrToken, captchaToken) => {
    try {
      // Validar datos de entrada antes del envío
      if (!attendanceData.documentNumber) {
        throw new Error("Número de documento es requerido");
      }

      if (!attendanceData.formData) {
        throw new Error("Datos del formulario son requeridos");
      }

      // Estructurar payload completo para el backend
      const payload = {
        documentNumber: attendanceData.documentNumber,
        recordType: attendanceData.recordType,
        formData: {
          // Datos personales básicos
          documentNumber: attendanceData.documentNumber,
          name: attendanceData.formData.name,
          visitorType: attendanceData.formData.visitorType,
          phone: attendanceData.formData.phone,
          
          // Datos de salud y seguridad
          eps: attendanceData.formData.eps,
          arl: attendanceData.formData.arl,
          jobPosition: attendanceData.formData.jobPosition,
          emergencyPhone: attendanceData.formData.emergencyPhone,
          
          // Datos de visita
          company: attendanceData.formData.company,
          visitArea: attendanceData.formData.visitArea,
          visitReason: attendanceData.formData.visitReason,
          visitReasonOther: attendanceData.formData.visitReasonOther,
          expectedExitTime: attendanceData.formData.expectedExitTime
        },
        isRegistered: attendanceData.isRegistered,
        captchaToken
      };

      // Agregar feedback de salida si está presente
      if (attendanceData.exitFeedback) {
        payload.exitFeedback = {
          purposeFulfilled: attendanceData.exitFeedback.purposeFulfilled,
          areaMetExpectations: attendanceData.exitFeedback.areaMetExpectations,
          additionalComments: attendanceData.exitFeedback.additionalComments || '',
          anonymousProcessing: attendanceData.exitFeedback.anonymousProcessing || false
        };
      }

      const response = await publicApi.post(
        "/attendance/register",
        payload,
        {
          params: { tk: qrToken }
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error registrando asistencia:", error);
      throw error.response?.data || { 
        success: false, 
        message: "Error al registrar asistencia en el sistema" 
      };
    }
  },

  /**
   * Registra que se mostró información HSEQ-SV al usuario
   * @param {string} attendanceRecordId - ID del registro de asistencia
   * @param {string} qrToken - Token QR para autorización
   * @returns {Promise} Confirmación del registro
   */
  recordInfoShown: async (attendanceRecordId, qrToken) => {
    try {
      if (!attendanceRecordId) {
        throw new Error("ID de registro de asistencia es requerido");
      }

      const response = await publicApi.post(
        "/attendance/record-info-shown",
        { attendanceRecordId },
        {
          params: { tk: qrToken }
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error registrando información mostrada:", error);
      throw error.response?.data || { 
        success: false, 
        message: "Error al registrar información HSEQ mostrada" 
      };
    }
  },

  /**
   * Obtiene el historial de asistencias de un usuario
   * @param {string} documentNumber - Número de documento
   * @param {string} qrToken - Token QR para autorización
   * @param {number} limit - Límite de registros (opcional)
   * @returns {Promise} Historial de asistencias
   */
  getUserHistory: async (documentNumber, qrToken, limit = 10) => {
    try {
      if (!documentNumber) {
        throw new Error("Número de documento es requerido");
      }

      const response = await publicApi.get(
        `/attendance/user-history/${documentNumber}`,
        {
          params: { 
            tk: qrToken,
            limit 
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error obteniendo historial de usuario:", error);
      throw error.response?.data || { 
        success: false, 
        message: "Error al obtener historial de asistencias" 
      };
    }
  },

  /**
   * Obtiene estadísticas del día actual (para uso administrativo)
   * @returns {Promise} Estadísticas del día
   */
  getTodayStatistics: async () => {
    try {
      const response = await publicApi.get("/attendance/today-stats");
      return response.data;
    } catch (error) {
      console.error("Error obteniendo estadísticas del día:", error);
      throw error.response?.data || { 
        success: false, 
        message: "Error al obtener estadísticas del día" 
      };
    }
  },

  /**
   * Valida campos del formulario de manera local
   * @param {Object} formData - Datos del formulario a validar
   * @param {boolean} isRegistered - Si el usuario está registrado
   * @returns {Object} Objeto con errores encontrados
   */
  validateFormData: (formData, isRegistered = false) => {
    const errors = {};

    // Validaciones para número de documento
    if (!formData.documentNumber || formData.documentNumber.length < 5) {
      errors.documentNumber = 'Número de documento inválido';
    }

    // Validaciones para usuarios no registrados
    if (!isRegistered) {
      if (!formData.name || formData.name.trim().length < 2) {
        errors.name = 'Nombre completo es requerido (mínimo 2 caracteres)';
      }

      if (!formData.visitorType) {
        errors.visitorType = 'Debe seleccionar el tipo de visitante';
      }

      if (!formData.phone || !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
        errors.phone = 'Teléfono debe tener 10 dígitos';
      }

      // Validación específica para visitantes que no son personal
      if (formData.visitorType && formData.visitorType !== 'personal') {
        if (!formData.visitArea || formData.visitArea.trim().length === 0) {
          errors.visitArea = 'Área a visitar es requerida';
        }
      }
    }

    // Validaciones opcionales para campos de salud
    if (formData.phone && formData.phone.length > 0 && !/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      errors.phone = 'Formato de teléfono inválido';
    }

    if (formData.emergencyPhone && formData.emergencyPhone.length > 0 && !/^\d{10}$/.test(formData.emergencyPhone.replace(/\D/g, ''))) {
      errors.emergencyPhone = 'Formato de teléfono de emergencia inválido';
    }

    // Validación de hora de salida esperada
    if (formData.expectedExitTime && !/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(formData.expectedExitTime)) {
      errors.expectedExitTime = 'Formato de hora inválido (HH:MM)';
    }

    return errors;
  },

  /**
   * Valida feedback de salida
   * @param {Object} exitFeedback - Datos del feedback a validar
   * @returns {Object} Objeto con errores encontrados
   */
  validateExitFeedback: (exitFeedback) => {
    const errors = {};

    if (exitFeedback.purposeFulfilled === null || exitFeedback.purposeFulfilled === undefined) {
      errors.purposeFulfilled = 'Debe responder si se cumplió el propósito de la visita';
    }

    if (exitFeedback.areaMetExpectations === null || exitFeedback.areaMetExpectations === undefined) {
      errors.areaMetExpectations = 'Debe responder si el área cumplió con las expectativas';
    }

    return errors;
  },

  /**
   * Formatea datos del usuario para mostrar en la interfaz
   * @param {Object} userData - Datos del usuario del backend
   * @returns {Object} Datos formateados para el frontend
   */
  formatUserDataForDisplay: (userData) => {
    if (!userData) return null;

    return {
      // Información básica
      fullName: userData.registered 
        ? `${userData.firstName} ${userData.lastName}`.trim()
        : userData.name || 'Usuario Anónimo',
      
      role: userData.role || 'Visitante',
      isStaff: userData.isStaff || false,
      isRegistered: userData.registered || false,
      
      // Campos de salud y seguridad con valores por defecto
      eps: userData.eps || '',
      arl: userData.arl || '',
      jobPosition: userData.jobPosition || '',
      emergencyPhone: userData.emergencyPhone || '',
      
      // Información de contacto de emergencia formateada
      emergencyContact: userData.emergencyContact ? {
        name: userData.emergencyContact.name,
        phone: userData.emergencyContact.phone || 'No disponible'
      } : null,
      
      // Campos faltantes identificados
      missingFields: {
        eps: !userData.eps,
        arl: !userData.arl,
        jobPosition: !userData.jobPosition,
        emergencyPhone: !userData.emergencyPhone
      }
    };
  },

  /**
   * Obtiene opciones predefinidas para motivos de visita
   * @returns {Array} Lista de motivos de visita disponibles
   */
  getVisitReasons: () => {
    return [
      { value: 'trabajo', label: 'Trabajo' },
      { value: 'entrega', label: 'Entrega de documentos' },
      { value: 'mantenimiento', label: 'Mantenimiento técnico' },
      { value: 'evento', label: 'Participación en evento' },
      { value: 'reclamos', label: 'Reclamos o trámites administrativos' },
      { value: 'consulta', label: 'Consulta' },
      { value: 'capacitacion', label: 'Capacitación' },
      { value: 'otro', label: 'Otro:' }
    ];
  },

  /**
   * Genera mensajes de bienvenida personalizados
   * @param {string} recordType - Tipo de registro ('entrada' o 'salida')
   * @param {boolean} isStaff - Si es personal de la empresa
   * @param {Object} lastRecord - Último registro del día
   * @returns {string} Mensaje personalizado
   */
  getWelcomeMessage: (recordType, isStaff, lastRecord) => {
    if (recordType === 'entrada') {
      if (isStaff) {
        const messages = [
          "¡Bienvenido de vuelta! Esperamos que tengas un excelente día",
          "¡Qué bueno verte! Tu presencia ilumina el lugar",
          "¡Hola! Listos para hacer grandes cosas hoy"
        ];
        return messages[Math.floor(Math.random() * messages.length)];
      } else if (lastRecord && lastRecord.type === 'salida') {
        return "¡Bienvenido de vuelta! Sabíamos que regresarías";
      } else {
        return "¡Bienvenido! Esperamos que tu visita sea productiva";
      }
    } else {
      if (isStaff) {
        return "¡Te esperamos mañana! Que tengas un excelente resto del día";
      } else {
        return "Gracias por tu visita. ¡Esperamos verte pronto!";
      }
    }
  }
};